import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';

import { canShowNavItem, isNavItemDisabled } from './navVisibility';
import type { NavVisibilityContext } from './navVisibility';
import type { NavConfig, NavBadgeCounts, NavItem } from '@/config/nav.types';
import type { RoleBadge } from '@/components/layout/AppTopbar';

/** Max items in the bottom bar */
const MAX_ITEMS = 5; // General max; producer portal uses strict 4-item rule via footerOverride

interface AppMobileBottomNavProps {
  navConfig: NavConfig;
  role: RoleBadge;
  badgeCounts?: NavBadgeCounts;
  visibilityCtx: NavVisibilityContext;
  /** Override items for the footer (e.g. producer portal has its own 5-tab footer) */
  footerOverride?: NavItem[];
}

/**
 * Flatten navConfig → first MAX_ITEMS visible, top-level items.
 * Children are excluded (bottom nav = top level only).
 */
function getPrimaryItems(navConfig: NavConfig, ctx: NavVisibilityContext): NavItem[] {
  const items: NavItem[] = [];
  for (const section of navConfig) {
    for (const item of section.items) {
      if (!canShowNavItem(item, ctx)) continue;
      items.push(item);
      if (items.length >= MAX_ITEMS) return items;
    }
  }
  return items;
}

/**
 * Mobile-only sticky bottom navigation bar.
 * Uses the same navConfig + visibility rules as AppSidebar.
 * Hidden on md+ where the sidebar handles navigation.
 */
export function AppMobileBottomNav({
  navConfig,
  role,
  badgeCounts = {},
  visibilityCtx,
  footerOverride,
}: AppMobileBottomNavProps) {
  const { pathname } = useLocation();
  const items = footerOverride ?? getPrimaryItems(navConfig, visibilityCtx);

  if (items.length === 0) return null;

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 inset-x-0 z-50 border-t border-border bg-background shadow-[0_-8px_24px_rgba(0,0,0,0.10)]"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
      aria-label="App-navigering"
    >
      <div className="mx-auto flex w-full max-w-md h-[72px] items-center justify-around px-2">
        {items.map((item) => {
          const isActive = item.exact
            ? pathname === item.href
            : pathname.startsWith(item.href) ||
              (item.activeMatchPrefixes?.some((p) => pathname.startsWith(p)) ?? false);

          const { disabled, reason } = isNavItemDisabled(item, visibilityCtx);
          const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] : undefined;
          const Icon = item.icon;

          if (disabled) {
            return (
              <div
                key={item.href}
                className="relative flex flex-col items-center justify-center gap-0.5 flex-1 min-h-[48px] rounded-md opacity-40 pointer-events-none"
                aria-disabled="true"
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
                {reason && (
                  <span className="absolute -top-1 right-1/2 translate-x-1/2 text-[8px] text-muted-foreground whitespace-nowrap">
                    {reason}
                  </span>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.href}
              to={item.href}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex flex-col items-center justify-center gap-0.5 flex-1 min-h-[48px] rounded-md transition-colors',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {badgeCount != null && badgeCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
